import { useCallback } from 'react';
import { DocumentNode, useApolloClient } from '@apollo/client';

import {
  KommoLeadCardDocument,
  KommoLeadEventListDocument,
  KommoLeadLadderListDocument,
  KommoLeadThreadListDocument,
  LeadSmsStateDocument,
} from 'core/graphql';

/**
 * Перепроверка 0084 — перечитать запросы карточки лида ПОСЛЕ удачного
 * действия, не заставляя его ждать.
 *
 * Раньше мутации ждали перечитывания (`awaitRefetchQueries`): письмо уже
 * ушло, а сбой любого из пяти перечитываний превращал успех в «Не удалось
 * отправить» — и человек жал ещё раз. Теперь ответ мутации решает исход
 * сам, а перечитывание идёт следом, и его ошибка глотается: экран
 * обновится следующим опросом или по кнопке.
 *
 * Запросы — по документу, а не по имени: по имени бьётся и пропущенный
 * запрос (грабли `refetch-by-name-skipped-query`).
 */

export interface ILeadRefetchTarget {
  query: DocumentNode;
  variables: Record<string, unknown>;
}

/** Что перечитать карточке лида: все пять — или часть. */
export const leadCardTargets = (
  leadId: string,
  parts: ReadonlyArray<'card' | 'ladders' | 'sms' | 'events' | 'threads'>,
): ILeadRefetchTarget[] =>
  parts.map((part) => {
    switch (part) {
      case 'card':
        return { query: KommoLeadCardDocument, variables: { id: leadId } };
      case 'ladders':
        return { query: KommoLeadLadderListDocument, variables: { leadId } };
      case 'sms':
        return { query: LeadSmsStateDocument, variables: { leadId } };
      case 'events':
        return { query: KommoLeadEventListDocument, variables: { leadId } };
      default:
        return { query: KommoLeadThreadListDocument, variables: { leadId } };
    }
  });

export const useQuietRefetch = () => {
  const client = useApolloClient();
  return useCallback(
    (targets: ReadonlyArray<ILeadRefetchTarget>) => {
      targets.forEach(({ query, variables }) => {
        client
          .query({ query, variables, fetchPolicy: 'network-only' })
          .catch(() => undefined);
      });
    },
    [client],
  );
};
